<!-- Copyright (c) 2009-2022. Authors: see NOTICE file.

 Licensed under the Apache License, Version 2.0 (the "License");
 you may not use this file except in compliance with the License.
 You may obtain a copy of the License at

      http://www.apache.org/licenses/LICENSE-2.0

 Unless required by applicable law or agreed to in writing, software
 distributed under the License is distributed on an "AS IS" BASIS,
 WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 See the License for the specific language governing permissions and
 limitations under the License.-->

<template>
<ims-modal :active="active" :title="$t('add-images')" class="add-image-modal" @close="$emit('update:active', false)">
  <b-loading :is-full-page="false" :active="loading" class="small" />
  <template v-if="!loading">
    <!-- toolbar: search · Filters (case-info aware) · Columns — mirrors the images page -->
    <div class="aim-toolbar">
      <div class="aim-search">
        <i class="fas fa-search"></i>
        <input :value="searchString" @input="e => debounceSearch(e.target.value)" :placeholder="$t('search')" type="search" />
      </div>

      <div class="aim-anchor" v-click-outside="() => closePanel('filters')">
        <button class="aim-ctrl" :class="{active: openPanel === 'filters'}" @click="togglePanel('filters')">
          <i class="fas fa-sliders-h"></i> {{ $t('filters') }}
          <span v-if="activeFilterCount" class="aim-badge">{{ activeFilterCount }}</span>
        </button>
        <div v-if="openPanel === 'filters'" class="aim-pop aim-pop-fb">
          <image-filter-builder :value="filters" :groups="filterableGroups" @input="filters = $event" />
        </div>
      </div>

      <div class="aim-anchor" v-click-outside="() => closePanel('columns')">
        <button class="aim-ctrl" :class="{active: openPanel === 'columns'}" @click="togglePanel('columns')">
          <i class="fas fa-columns"></i> {{ $t('columns') }}
        </button>
        <div v-if="openPanel === 'columns'" class="aim-pop aim-pop-cm">
          <label v-for="c in optionalColumns" :key="c.key" class="cm-item" @click.prevent="toggleCol(c.key)">
            <span class="cm-check" :class="{on: visibleCols[c.key]}"><i v-if="visibleCols[c.key]" class="fas fa-check"></i></span>
            <span class="cm-name">{{ $t(c.labelKey) }}</span>
          </label>
        </div>
      </div>
    </div>

    <ims-table
      :collection="imageCollection"
      :currentPage.sync="currentPage"
      :perPage.sync="perPage"
      :sort.sync="sortField"
      :order.sync="sortOrder"
      :detailed="false"
    >
      <template #default="{row: image}">
        <b-table-column :label="$t('overview')" width="140">
          <image-thumbnail :image="image" :size="128" :key="`${image.id}-thumb-128`" :extra-parameters="{authorization: 'Bearer ' + shortTermToken }"/>
        </b-table-column>

        <b-table-column field="originalFilename" :label="$t('name')" sortable>
          {{ image.originalFilename }}
        </b-table-column>

        <b-table-column field="created" :label="$t('created-on')" sortable>
          {{ Number(image.created) | moment('ll LT') }}
        </b-table-column>

        <b-table-column v-if="visibleCols.magnification" :label="$t('magnification')">
          {{ image.magnification || '—' }}
        </b-table-column>
        <b-table-column v-if="visibleCols.format" :label="$t('format')">
          {{ image.contentType || '—' }}
        </b-table-column>
        <b-table-column v-if="visibleCols.width" :label="$t('image-width')">
          {{ image.width }}
        </b-table-column>
        <b-table-column v-if="visibleCols.height" :label="$t('image-height')">
          {{ image.height }}
        </b-table-column>

        <b-table-column label=" " centered>
          <button v-if="wasAdded(image)" class="button is-small is-link" disabled>
            {{$t('button-added')}}
          </button>
          <span v-else-if="isInProject(image)">
            {{$t('already-in-project')}}
          </span>
          <button v-else class="button is-small is-link" @click="addImage(image)">
            {{$t('button-add')}}
          </button>
        </b-table-column>
      </template>

      <template #empty>
        <div class="content has-text-grey has-text-centered">
          <p>{{$t('no-image')}}</p>
        </div>
      </template>
    </ims-table>
  </template>
</ims-modal>
</template>

<script>
import {get} from '@/utils/store-helpers';
import {AbstractImageCollection, ImageInstance} from '@/api';
import {COLUMNS, COLUMN_GROUPS} from '@/utils/image-table-columns';
import {buildImageFilterExpression} from '@/utils/image-search';
import ImsModal from '@/components/utils/ImsModal';
import ImsTable from '@/components/utils/ImsTable';
import ImageThumbnail from '@/components/image/ImageThumbnail';
import ImageFilterBuilder from '@/components/image/ImageFilterBuilder';
import _ from 'lodash';

// Add-Image lists shared AbstractImages: you can FILTER by name + case-info (the latter via
// a backend EXISTS on the slide's instance), but rows display abstract-image fields only —
// case-info has no single value per shared abstract image, so it's not a display column.
const OPTIONAL_COLUMNS = [
  {key: 'magnification', labelKey: 'magnification'},
  {key: 'format', labelKey: 'format'},
  {key: 'width', labelKey: 'image-width'},
  {key: 'height', labelKey: 'image-height'}
];

export default {
  name: 'add-image-modal',
  props: {
    active: Boolean,
  },
  components: {
    ImageThumbnail,
    ImsTable,
    ImsModal,
    ImageFilterBuilder
  },
  data() {
    return {
      loading: true,
      perPage: 10,
      searchString: '',
      idsAddedImages: [],
      currentPage: 1,
      sortField: 'created',
      sortOrder: 'desc',
      filters: [],
      openPanel: null, // 'filters' | 'columns' | null
      optionalColumns: OPTIONAL_COLUMNS,
      visibleCols: {magnification: false, format: false, width: false, height: false}
    };
  },
  computed: {
    project: get('currentProject/project'),
    shortTermToken: get('currentUser/shortTermToken'),
    // Filterable fields offered in the builder: name + every case-info column (the only
    // fields the abstract-image backend filter supports). Other image columns are excluded.
    filterableGroups() {
      return COLUMN_GROUPS
        .map(g => ({...g, cols: COLUMNS.filter(c => c.filterable && (c.key === 'name' || c.group !== 'image'))
          .filter(c => c.group === g.id)}))
        .filter(g => g.cols.length);
    },
    activeFilterCount() {
      return this.filters.filter(f => f.operator === 'is-empty' || (f.value !== '' && f.value != null)).length;
    },
    imageCollection() {
      let collection = new AbstractImageCollection({
        project: this.project.id,
      });
      // single character is ignored (min length 2) to avoid a wide multi-field scan
      const search = this.searchString.trim().length === 1 ? '' : this.searchString;
      const expr = buildImageFilterExpression({search, builderFilters: this.filters});
      if (expr.length) {
        collection.filterExpression = JSON.stringify(expr);
      }
      return collection;
    },
  },
  watch: {
    active(val) {
      if (val) {
        this.idsAddedImages = [];
      }
    }
  },
  methods: {
    debounceSearch: _.debounce(function (value) {
      this.searchString = value;
    }, 400),
    togglePanel(name) {
      this.openPanel = this.openPanel === name ? null : name;
    },
    closePanel(name) {
      if (this.openPanel === name) {
        this.openPanel = null;
      }
    },
    toggleCol(key) {
      this.$set(this.visibleCols, key, !this.visibleCols[key]);
    },
    async addImage(abstractImage) {
      let propsTranslation = {imageName: abstractImage.originalFilename, projectName: this.project.name};
      try {
        let image = await new ImageInstance({baseImage: abstractImage.id, project: this.project.id}).save();
        this.idsAddedImages.push(abstractImage.id);
        this.$emit('addImage', image);
        this.$notify({
          type: 'success',
          text: this.$t('notif-success-add-image', propsTranslation)
        });

        let updatedProject = this.project.clone();
        updatedProject.numberOfImages++;
        this.$store.dispatch('currentProject/updateProject', updatedProject);
      } catch (error) {
        console.log(error);
        this.$notify({
          type: 'error',
          text: this.$t('notif-error-add-image', propsTranslation)
        });
      }
    },
    isInProject(image) {
      return image.inProject;
    },
    wasAdded(image) {
      return this.idsAddedImages.includes(image.id);
    }
  },
  async created() {
    this.loading = false;
  }
};
</script>

<style scoped>
/* modern, rounded modal chrome to match the images page / ImageDetailModal */
>>> .animation-content {
  max-width: 980px !important;
  width: 92%;
}
>>> .modal-card {
  width: 100%;
  height: 82vh;
  border-radius: 14px;
  overflow: hidden;
}
>>> .modal-card-head {
  border-bottom: 1px solid #e8eaef;
  background: #fff;
}
>>> .image-thumbnail {
  max-height: 4rem;
  max-width: 10rem;
  border-radius: 6px;
}

/* toolbar */
.aim-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 16px;
}
.aim-search {
  display: flex;
  align-items: center;
  gap: 9px;
  height: 40px;
  flex: 1;
  min-width: 220px;
  max-width: 420px;
  border: 1px solid #e6eaf0;
  border-radius: 10px;
  padding: 0 12px;
  color: #94a3b8;
}
.aim-search input { border: none; background: transparent; outline: none; width: 100%; font-size: 14px; color: #0f172a; }
.aim-anchor { position: relative; }
.aim-ctrl {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 14px;
  border-radius: 10px;
  border: 1px solid #e6eaf0;
  background: #fff;
  color: #334155;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
}
.aim-ctrl:hover { background: #f8fafc; }
.aim-ctrl.active { border-color: #1f6fe5; background: #f4f7fe; color: #1f6fe5; }
.aim-badge {
  background: #1f6fe5;
  color: #fff;
  font-size: 11px;
  font-weight: 800;
  min-width: 18px;
  height: 18px;
  border-radius: 9px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 5px;
}
.aim-pop {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  z-index: 40;
  background: #fff;
  border: 1px solid #e8eaef;
  border-radius: 14px;
  box-shadow: 0 18px 48px rgba(31, 41, 55, 0.16);
}
.aim-pop-fb { min-width: 680px; max-width: 90vw; padding: 16px 18px; }
.aim-pop-cm { min-width: 240px; padding: 8px; }
.cm-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 10px;
  border-radius: 8px;
  cursor: pointer;
}
.cm-item:hover { background: #f4f6fa; }
.cm-check {
  width: 18px;
  height: 18px;
  border-radius: 5px;
  border: 1.7px solid #cbd2dc;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 10px;
}
.cm-check.on { background: #1f6fe5; border-color: #1f6fe5; }
.cm-name { font-size: 14px; color: #334155; }
</style>
